<template>
  <Tag :value="displayRole" :severity="roleSeverity" :icon="roleIcon" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Tag from 'primevue/tag'
import type { UserRole } from '@/types/permissions'

interface Props {
  role: UserRole
  showIcon?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showIcon: true,
})

/**
 * Display name for the role
 */
const displayRole = computed(() => {
  const roleNames: Record<UserRole, string> = {
    admin: 'Admin',
    sales: 'Sales',
    deliverer: 'Deliverer',
    accountant: 'Accountant',
  }
  return roleNames[props.role] || props.role
})

/**
 * PrimeVue Tag severity based on role
 */
const roleSeverity = computed(() => {
  const severities: Record<UserRole, 'danger' | 'success' | 'info' | 'warning'> = {
    admin: 'danger', // Red - highest privilege
    sales: 'success', // Green - operational role
    deliverer: 'info', // Blue - field role
    accountant: 'warning', // Orange/Yellow - financial role
  }
  return severities[props.role] || 'info'
})

/**
 * Icon for the role (optional)
 */
const roleIcon = computed(() => {
  if (!props.showIcon) return undefined

  const icons: Record<UserRole, string> = {
    admin: 'pi pi-shield',
    sales: 'pi pi-shopping-cart',
    deliverer: 'pi pi-truck',
    accountant: 'pi pi-dollar',
  }
  return icons[props.role]
})
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
